import { useEffect, useMemo, useRef, useState } from 'react'
import { Camera, CheckCircle2, FileText, Plus, Route, Truck } from 'lucide-react'
import { PageContainer, PageHeader } from '../../components/Layout'
import { Button, Card, CardContent, Input, Select, Badge, Modal } from '../../components/UI'
import { rentalTechService } from '../../services/api'
import { equipmentOptions, findEquipmentById, useRentalTechData } from './useRentalTechData'

const inspectionTypeOptions = [
  { value: 'entrada', label: 'Acta de entrada' },
  { value: 'salida', label: 'Acta de salida' }
]

const checklistTemplate = [
  { item: 'Frenos', ok: true },
  { item: 'Luces', ok: true },
  { item: 'Nivel de aceite', ok: true },
  { item: 'Sistema hidraulico', ok: true },
  { item: 'Cabina / controles', ok: true }
]

const createChecklist = () => checklistTemplate.map((item) => ({ ...item }))

const initialActaForm = (type = 'salida') => ({
  equipmentId: '',
  rentalOrderId: '',
  type,
  signatureName: 'Supervisor Campo',
  checklist: createChecklist(),
  photoUrls: []
})

const toDateLabel = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

const parseJsonArray = (raw, fallback = []) => {
  try {
    const parsed = JSON.parse(raw || '[]')
    return Array.isArray(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

function ProcessStep({ title, subtitle, state = 'pending' }) {
  const styles = {
    done: 'bg-green-50 border-green-200 text-green-800',
    current: 'bg-blue-50 border-blue-200 text-blue-800',
    pending: 'bg-gray-50 border-gray-200 text-gray-600'
  }

  return (
    <div className={`rounded-lg border px-3 py-2 ${styles[state] || styles.pending}`}>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs opacity-80">{subtitle}</p>
    </div>
  )
}

function ActaFormModal({
  isOpen,
  onClose,
  title,
  form,
  setForm,
  equipments,
  orders,
  onSubmit,
  submitting
}) {
  const fileInputRef = useRef(null)
  const equipmentSelectOptions = useMemo(() => equipmentOptions(equipments), [equipments])
  const orderOptions = useMemo(
    () => (orders || []).map((o) => ({ value: o.id, label: `${o.contract_number || o.quote_number} - ${o.customer_name}` })),
    [orders]
  )

  useEffect(() => {
    if (isOpen && !form.equipmentId && equipmentSelectOptions.length > 0) {
      setForm((prev) => ({ ...prev, equipmentId: equipmentSelectOptions[0].value }))
    }
  }, [isOpen, form.equipmentId, equipmentSelectOptions, setForm])

  const handleChecklistToggle = (idx, checked) => {
    setForm((prev) => ({
      ...prev,
      checklist: prev.checklist.map((item, i) => (i === idx ? { ...item, ok: checked } : item))
    }))
  }

  const handlePhotoFiles = async (files) => {
    const readers = Array.from(files || []).map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(String(reader.result || ''))
          reader.onerror = () => resolve('')
          reader.readAsDataURL(file)
        })
    )

    const results = (await Promise.all(readers)).filter(Boolean)
    if (!results.length) return

    setForm((prev) => ({ ...prev, photoUrls: [...prev.photoUrls, ...results] }))
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary-600" />
          <span>{title}</span>
        </div>
      }
    >
      <div className="space-y-5">
        <div>
          <h4 className="text-base font-semibold text-gray-900">Datos del acta</h4>
          <p className="text-sm text-gray-500">Registra entrada o salida, checklist, firma y evidencia fotogr�fica.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Maquina / equipo"
            options={equipmentSelectOptions}
            value={form.equipmentId}
            onChange={(e) => setForm((prev) => ({ ...prev, equipmentId: e.target.value }))}
            placeholder="Seleccionar maquina"
          />
          <Select
            label="Tipo de acta"
            options={inspectionTypeOptions}
            value={form.type}
            onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
            placeholder={null}
          />
          <Select
            label="Contrato relacionado (opcional)"
            options={orderOptions}
            value={form.rentalOrderId || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, rentalOrderId: e.target.value }))}
            placeholder="Seleccionar contrato"
          />
          <Input
            label="Responsable / firma"
            value={form.signatureName}
            onChange={(e) => setForm((prev) => ({ ...prev, signatureName: e.target.value }))}
            placeholder="Nombre del responsable en campo"
          />
        </div>

        <div className="rounded-lg border border-gray-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-semibold text-gray-900">Checklist tecnico</h5>
            <Badge variant="info">{form.checklist.filter((x) => x.ok).length}/{form.checklist.length} OK</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {form.checklist.map((item, idx) => (
              <label key={`${item.item}-${idx}`} className="flex items-center justify-between gap-3 rounded border border-gray-200 px-3 py-2 text-sm">
                <span>{item.item}</span>
                <input
                  type="checkbox"
                  checked={!!item.ok}
                  onChange={(e) => handleChecklistToggle(idx, e.target.checked)}
                />
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h5 className="text-sm font-semibold text-gray-900">Registro fotografico</h5>
              <p className="text-xs text-gray-500">Puedes cargar varias fotos de evidencia (entrada/salida, estado de m�quina, da�os, accesorios).</p>
            </div>
            <Button variant="outline" leftIcon={<Camera className="w-4 h-4" />} onClick={() => fileInputRef.current?.click()}>
              Agregar fotos
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              handlePhotoFiles(e.target.files)
              e.target.value = ''
            }}
          />

          {form.photoUrls.length ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {form.photoUrls.map((url, idx) => (
                <div key={`${idx}-${url.slice(0, 16)}`} className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                  <img src={url} alt={`foto-${idx + 1}`} className="w-full h-24 object-cover" />
                  <div className="p-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setForm((prev) => ({ ...prev, photoUrls: prev.photoUrls.filter((_, i) => i !== idx) }))}
                    >
                      Quitar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded border border-dashed border-gray-300 p-4 text-sm text-gray-500">
              Aun no hay fotos cargadas para el acta.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={onSubmit} loading={submitting} leftIcon={<CheckCircle2 className="w-4 h-4" />}>
            Generar acta
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default function RentalTechMobileOps() {
  const { equipments, orders } = useRentalTechData()
  const [inspections, setInspections] = useState([])
  const [selectedEquipmentId, setSelectedEquipmentId] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const [showActaModal, setShowActaModal] = useState(false)
  const [actaMode, setActaMode] = useState('salida')
  const [actaForm, setActaForm] = useState(initialActaForm('salida'))
  const [savingActa, setSavingActa] = useState(false)

  const equipmentSelectOptions = useMemo(() => equipmentOptions(equipments), [equipments])

  const loadInspections = async () => {
    const response = await rentalTechService.getInspections()
    setInspections(response?.data?.data || [])
  }

  useEffect(() => {
    loadInspections()
  }, [])

  useEffect(() => {
    if (!selectedEquipmentId && equipmentSelectOptions.length > 0) {
      setSelectedEquipmentId(equipmentSelectOptions[0].value)
    }
  }, [equipmentSelectOptions, selectedEquipmentId])

  const machineOrders = useMemo(
    () => (orders || []).filter((o) => !selectedEquipmentId || o.equipment_id === selectedEquipmentId),
    [orders, selectedEquipmentId]
  )

  const machineInspections = useMemo(
    () => (inspections || []).filter((i) => !selectedEquipmentId || i.equipment_id === selectedEquipmentId),
    [inspections, selectedEquipmentId]
  )

  const currentEquipment = useMemo(
    () => findEquipmentById(equipments, selectedEquipmentId) || null,
    [equipments, selectedEquipmentId]
  )

  const processEvents = useMemo(() => {
    const contractEvents = machineOrders.flatMap((order) => [
      {
        id: `${order.id}-contract`,
        when: order.created_at_utc || order.start_utc,
        kind: 'contrato',
        title: `Contrato ${order.contract_number || order.quote_number}`,
        subtitle: `${order.customer_name} | ${order.signed ? 'Firmado' : 'Pendiente firma'}`,
        badge: order.signed ? 'success' : 'warning'
      },
      {
        id: `${order.id}-schedule`,
        when: order.start_utc,
        kind: 'programacion',
        title: 'Programacion de salida a campo',
        subtitle: `${toDateLabel(order.start_utc)} -> ${toDateLabel(order.end_utc)}`,
        badge: 'info'
      }
    ])

    const inspectionEvents = machineInspections.map((inspection) => ({
      id: inspection.id,
      when: inspection.created_at_utc,
      kind: inspection.type,
      title: inspection.type === 'entrada' ? 'Acta de entrada registrada' : 'Acta de salida registrada',
      subtitle: `Responsable: ${inspection.signature_name || '-'} | Fotos: ${parseJsonArray(inspection.photos_json).length}`,
      badge: inspection.type === 'entrada' ? 'success' : 'primary',
      inspection
    }))

    return [...contractEvents, ...inspectionEvents].sort((a, b) => new Date(a.when || 0) - new Date(b.when || 0))
  }, [machineOrders, machineInspections])

  const latestSalida = [...machineInspections].filter((x) => x.type === 'salida').sort((a, b) => new Date(b.created_at_utc) - new Date(a.created_at_utc))[0]
  const latestEntrada = [...machineInspections].filter((x) => x.type === 'entrada').sort((a, b) => new Date(b.created_at_utc) - new Date(a.created_at_utc))[0]

  const processState = useMemo(() => {
    if (!currentEquipment) return { steps: [], current: 'Sin maquina' }
    const hasContrato = machineOrders.length > 0
    const hasSalida = !!latestSalida
    const hasEntradaAfterSalida = !!(latestEntrada && latestSalida && new Date(latestEntrada.created_at_utc) > new Date(latestSalida.created_at_utc))

    return {
      current: hasEntradaAfterSalida ? 'Retornado con acta de entrada' : hasSalida ? 'En operacion / seguimiento' : hasContrato ? 'Contrato generado, pendiente acta de salida' : 'Disponible / sin movimiento reciente',
      steps: [
        { title: 'Contrato / reserva', subtitle: hasContrato ? `${machineOrders.length} registro(s)` : 'Sin contratos', state: hasContrato ? 'done' : 'current' },
        { title: 'Acta de salida', subtitle: hasSalida ? toDateLabel(latestSalida.created_at_utc) : 'Pendiente', state: hasSalida ? 'done' : hasContrato ? 'current' : 'pending' },
        { title: 'Operacion en campo', subtitle: hasSalida && !hasEntradaAfterSalida ? 'Seguimiento activo' : hasEntradaAfterSalida ? 'Completado' : 'Sin iniciar', state: hasSalida && !hasEntradaAfterSalida ? 'current' : hasEntradaAfterSalida ? 'done' : 'pending' },
        { title: 'Acta de entrada', subtitle: hasEntradaAfterSalida ? toDateLabel(latestEntrada.created_at_utc) : 'Pendiente retorno', state: hasEntradaAfterSalida ? 'done' : hasSalida ? 'current' : 'pending' }
      ]
    }
  }, [currentEquipment, machineOrders, latestSalida, latestEntrada])

  const openActaModal = (type) => {
    setActaMode(type)
    setActaForm((prev) => ({
      ...initialActaForm(type),
      equipmentId: selectedEquipmentId || prev.equipmentId || '',
      rentalOrderId: machineOrders[0]?.id || ''
    }))
    setShowActaModal(true)
    setError('')
    setMessage('')
  }

  const createActa = async () => {
    if (!actaForm.equipmentId) {
      setError('Debes seleccionar una maquina para generar el acta.')
      return
    }

    setSavingActa(true)
    setError('')
    setMessage('')
    try {
      const payload = {
        equipmentId: actaForm.equipmentId,
        rentalOrderId: actaForm.rentalOrderId || null,
        type: actaForm.type,
        checklistJson: JSON.stringify(actaForm.checklist),
        photosJson: JSON.stringify(actaForm.photoUrls),
        signatureName: actaForm.signatureName
      }

      const response = await rentalTechService.createInspection(payload)
      const created = response?.data?.data || null
      await loadInspections()
      setShowActaModal(false)
      setMessage(`Acta de ${actaForm.type} generada correctamente.${created?.pdf_report_url ? ' PDF disponible.' : ''}`)
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'No fue posible generar el acta.')
    } finally {
      setSavingActa(false)
    }
  }

  const recentPhotos = useMemo(
    () => machineInspections.flatMap((inspection) => parseJsonArray(inspection.photos_json).map((url, idx) => ({
      key: `${inspection.id}-${idx}`,
      url,
      type: inspection.type,
      createdAt: inspection.created_at_utc
    }))).slice(0, 24),
    [machineInspections]
  )

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Mobile Ops - Movimiento de Maquinas"
          subtitle="Actas de entrada/salida, registro fotografico y flujo por maquina para seguimiento operativo"
        />

        {message ? <div className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded px-3 py-2">{message}</div> : null}
        {error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div> : null}

        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end lg:justify-between">
              <div className="w-full lg:max-w-xl">
                <Select
                  label="Seleccionar maquina para seguimiento"
                  options={equipmentSelectOptions}
                  value={selectedEquipmentId}
                  onChange={(e) => setSelectedEquipmentId(e.target.value)}
                  placeholder="Seleccionar maquina"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="primary" leftIcon={<Truck className="w-4 h-4" />} onClick={() => openActaModal('salida')}>
                  Hacer acta de salida
                </Button>
                <Button variant="secondary" leftIcon={<Route className="w-4 h-4" />} onClick={() => openActaModal('entrada')}>
                  Hacer acta de entrada
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div className="xl:col-span-2 rounded-lg border border-gray-200 p-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Flujo por maquina (proceso)</h3>
                    <p className="text-xs text-gray-500">Seguimiento del movimiento operativo desde contrato hasta retorno.</p>
                  </div>
                  <Badge variant="info">{currentEquipment?.name || 'Sin seleccion'}</Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {processState.steps.map((step, idx) => (
                    <ProcessStep key={`${step.title}-${idx}`} {...step} />
                  ))}
                </div>

                <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                  Estado actual: <strong>{processState.current}</strong>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Resumen de movimiento</h3>
                <div className="text-sm space-y-2">
                  <div className="flex items-center justify-between"><span className="text-gray-500">Contratos</span><span>{machineOrders.length}</span></div>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Actas salida</span><span>{machineInspections.filter((x) => x.type === 'salida').length}</span></div>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Actas entrada</span><span>{machineInspections.filter((x) => x.type === 'entrada').length}</span></div>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Ultima salida</span><span className="text-xs">{latestSalida ? toDateLabel(latestSalida.created_at_utc) : '-'}</span></div>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Ultima entrada</span><span className="text-xs">{latestEntrada ? toDateLabel(latestEntrada.created_at_utc) : '-'}</span></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-5 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900">Historial del proceso por maquina</h3>
                <p className="text-xs text-gray-500">L�nea de tiempo de contratos, programaciones y actas para seguimiento del movimiento.</p>
              </div>

              <div className="space-y-3 max-h-[520px] overflow-auto pr-1">
                {processEvents.map((event) => (
                  <div key={event.id} className="relative pl-5">
                    <div className="absolute left-0 top-2 h-full w-px bg-gray-200" />
                    <div className="absolute left-[-4px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary-500" />
                    <div className="rounded-lg border border-gray-200 p-3 bg-white">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{event.title}</p>
                          <p className="text-xs text-gray-500">{event.subtitle}</p>
                          <p className="text-xs text-gray-400 mt-1">{toDateLabel(event.when)}</p>
                        </div>
                        <Badge variant={event.badge || 'gray'}>{event.kind}</Badge>
                      </div>
                      {event.inspection ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          <a href={rentalTechService.getInspectionPdfUrl(event.inspection.id)} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="ghost" leftIcon={<FileText className="w-3.5 h-3.5" />}>PDF acta</Button>
                          </a>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
                {!processEvents.length ? <p className="text-sm text-gray-500">No hay eventos para la maquina seleccionada.</p> : null}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-900">Registro fotografico</h3>
                  <p className="text-xs text-gray-500">Evidencia visual asociada a actas de entrada y salida.</p>
                </div>
                <Badge variant="info">{recentPhotos.length} foto(s)</Badge>
              </div>

              {recentPhotos.length ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[520px] overflow-auto pr-1">
                  {recentPhotos.map((photo) => (
                    <div key={photo.key} className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                      <img src={photo.url} alt="registro-fotografico" className="w-full h-28 object-cover" />
                      <div className="p-2 space-y-1">
                        <Badge variant={photo.type === 'entrada' ? 'success' : 'primary'}>{photo.type}</Badge>
                        <p className="text-[11px] text-gray-500">{toDateLabel(photo.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                  No hay fotos registradas para esta maquina. Genera un acta de entrada/salida y agrega evidencia.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <ActaFormModal
          isOpen={showActaModal}
          onClose={() => setShowActaModal(false)}
          title={actaMode === 'entrada' ? 'Hacer acta de entrada' : 'Hacer acta de salida'}
          form={actaForm}
          setForm={setActaForm}
          equipments={equipments}
          orders={machineOrders}
          onSubmit={createActa}
          submitting={savingActa}
        />
      </div>
    </PageContainer>
  )
}
